import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { useCalendarStore } from '../hooks/useCalendarStore';
import { CALENDAR_FILTER_CATEGORY_COLORS } from '../types';
import type { CalendarFilterCategory, CalendarView } from '../types';

interface CalendarToolbarProps {
  label: string;
  onNavigate: (action: 'PREV' | 'NEXT' | 'TODAY') => void;
  onView: (view: CalendarView) => void;
  hideFilters?: boolean;
  hideViewSelector?: boolean;
}

const VIEWS: CalendarView[] = ['month', 'week', 'work_week', 'day', 'agenda'];

const EVENT_FILTER_ITEMS: { category: CalendarFilterCategory; labelKey: string }[] = [
  { category: 'task', labelKey: 'toolbar.filters.task' },
  { category: 'google', labelKey: 'toolbar.filters.google' },
  { category: 'microsoft', labelKey: 'toolbar.filters.microsoft' },
  { category: 'internal', labelKey: 'toolbar.filters.internal' },
  { category: 'native', labelKey: 'toolbar.filters.native' },
];

export function CalendarToolbar({
  label,
  onNavigate,
  onView,
  hideFilters,
  hideViewSelector,
}: CalendarToolbarProps) {
  const { t } = useTranslation('calendar');
  const { view, setView, activeFilters, toggleFilter } = useCalendarStore();

  function handleView(value: string) {
    const next = value as CalendarView;
    setView(next);
    onView(next);
  }

  return (
    <div data-testid="calendar-toolbar">
      {/* Fila principal: navegación + título + selector de vista */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        {/* Navegación izquierda */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="calendar-nav-prev"
                onClick={() => onNavigate('PREV')}
                aria-label={t('toolbar.previous')}
                className="h-8 w-8"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('toolbar.previous')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="calendar-nav-next"
                onClick={() => onNavigate('NEXT')}
                aria-label={t('toolbar.next')}
                className="h-8 w-8"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('toolbar.next')}</TooltipContent>
          </Tooltip>

          <Button
            variant="outline"
            size="sm"
            data-testid="calendar-nav-today"
            onClick={() => onNavigate('TODAY')}
            className="ml-1 h-8 text-xs"
          >
            {t('toolbar.today')}
          </Button>
        </div>

        {/* Título del período — centro */}
        <span className="text-sm font-semibold capitalize text-foreground">{label}</span>

        {/* Selector de vista — oculto cuando el consumidor solo soporta mes (ej. ausencias) */}
        {!hideViewSelector && (
          <Select value={view} onValueChange={handleView}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="calendar-view-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VIEWS.map((v) => (
                <SelectItem key={v} value={v} className="text-xs">
                  {t(`toolbar.views.${v === 'work_week' ? 'workWeek' : v}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Fila de filtros por tipo de evento — oculta en modo embedded */}
      {!hideFilters && (
        <div className="flex items-center gap-1.5 border-b border-border px-4 py-2">
          {EVENT_FILTER_ITEMS.map(({ category, labelKey }) => {
            const active = activeFilters.includes(category);
            const color = CALENDAR_FILTER_CATEGORY_COLORS[category];
            return (
              <button
                type="button"
                key={category}
                data-testid={`calendar-filter-${category}`}
                onClick={() => toggleFilter(category)}
                aria-pressed={active}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
                  active
                    ? cn('border-transparent text-white', color)
                    : 'border-border bg-transparent text-muted-foreground hover:bg-muted',
                )}
              >
                <span className={cn('h-1.5 w-1.5 rounded-full', active ? 'bg-white/70' : color)} />
                {t(labelKey)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
